"use client";

import type { Column } from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  EyeOff,
  X,
  Pin,
  PinOff,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/** How sort direction labels read in the column menu */
export type ColumnSortVariant = "alphabetical" | "chronological";

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.ComponentProps<typeof DropdownMenuTrigger> {
  column: Column<TData, TValue>;
  label: string;
  /** Default: alphabetical (A–Z / Z–A) */
  sortVariant?: ColumnSortVariant;
}

const SORT_LABELS: Record<
  ColumnSortVariant,
  { asc: string; desc: string; ascFirst: boolean }
> = {
  alphabetical: {
    asc: "A-Z",
    desc: "Z-A",
    ascFirst: true,
  },
  chronological: {
    asc: "Oldest to Latest",
    desc: "Latest to Oldest",
    ascFirst: false,
  },
};

export function DataTableColumnHeader<TData, TValue>({
  column,
  label,
  sortVariant = "alphabetical",
  className,
  ...props
}: DataTableColumnHeaderProps<TData, TValue>) {
  const isPinned = column.getIsPinned() === "left";
  const sortLabels = SORT_LABELS[sortVariant];

  const sortOptions = sortLabels.ascFirst
    ? [
        { dir: "asc" as const, label: sortLabels.asc, icon: ChevronUp },
        { dir: "desc" as const, label: sortLabels.desc, icon: ChevronDown },
      ]
    : [
        { dir: "desc" as const, label: sortLabels.desc, icon: ChevronDown },
        { dir: "asc" as const, label: sortLabels.asc, icon: ChevronUp },
      ];
  
  if (!column.getCanSort() && !column.getCanHide()) {
    return (
      <div className={cn("flex items-center justify-between gap-2", className)}>
        <span>{label}</span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (isPinned) {
              column.pin(false);
            } else {
              column.pin("left");
            }
          }}
          className={cn(
            "opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-accent",
            isPinned && "opacity-100"
          )}
          title={isPinned ? "Unpin column" : "Pin column"}
        >
          {isPinned ? (
            <PinOff className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <Pin className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 group">
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            "-ml-1.5 flex h-8 items-center gap-1.5 rounded-md px-2 py-1.5 hover:bg-accent focus:outline-none focus:ring-1 focus:ring-ring data-[state=open]:bg-accent [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground",
            className,
          )}
          {...props}
        >
          {label}
          {column.getCanSort() &&
            (column.getIsSorted() === "desc" ? (
              <ChevronDown />
            ) : column.getIsSorted() === "asc" ? (
              <ChevronUp />
            ) : (
              <ChevronsUpDown />
            ))}
        </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        {column.getCanSort() && (
          <>
            {sortOptions.map(({ dir, label: optionLabel, icon: Icon }) => (
              <DropdownMenuCheckboxItem
                key={dir}
                className="relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-muted-foreground"
                checked={column.getIsSorted() === dir}
                onClick={() => column.toggleSorting(dir === "desc")}
              >
                <Icon />
                {optionLabel}
              </DropdownMenuCheckboxItem>
            ))}
            {column.getIsSorted() && (
              <DropdownMenuItem
                className="pl-2 [&_svg]:text-muted-foreground"
                onClick={() => column.clearSorting()}
              >
                <X />
                Reset sort
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
          </>
        )}
        {column.getCanHide() && (
          <DropdownMenuCheckboxItem
            className="relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-muted-foreground"
            checked={!column.getIsVisible()}
            onClick={() => column.toggleVisibility(false)}
          >
            <EyeOff />
            Hide column
          </DropdownMenuCheckboxItem>
        )}
        <DropdownMenuCheckboxItem
          className="relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-muted-foreground"
          checked={column.getIsPinned() === "left"}
          onClick={() => {
            if (column.getIsPinned() === "left") {
              column.pin(false);
            } else {
              column.pin("left");
            }
          }}
        >
          {column.getIsPinned() === "left" ? <PinOff /> : <Pin />}
          {column.getIsPinned() === "left" ? "Unpin column" : "Pin column"}
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (isPinned) {
          column.pin(false);
        } else {
          column.pin("left");
        }
      }}
      className={cn(
        "opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-accent",
        isPinned && "opacity-100"
      )}
      title={isPinned ? "Unpin column" : "Pin column"}
    >
      {isPinned ? (
        <PinOff className="h-3.5 w-3.5 text-muted-foreground" />
      ) : (
        <Pin className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </button>
    </div>
  );
}
